import React from 'react';
import classNames from 'classnames';
import {observer} from 'mobx-react';

import {useI18n} from '@udemy-v2/i18n';
import {useMatchMedia} from '@udemy-v2/design-system-utils';
import {AsyncStaticPriceText, useCoursePrice} from '@udemy-v2/react-course-pricing';
import {TextSkeleton} from '@udemy-v2/react-reveal-components';
import {
    PRICE_EVENTING_BUYABLE_TYPES,
    PriceEventTrackingProvider,
    PURCHASE_PRICE_TYPES,
} from '@udemy-v2/react-currency-formatting';
import {AddToCartButton, WishlistButton} from '@udemy-v2/react-shopping-buttons';

import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {ClpPrimaryCtaButton} from '@/features/course-landing-page/components/clp-primary-cta-button/clp-primary-cta-button.react-component';
import {ClpCartSuccessModal} from '@/features/course-landing-page/components/clp-cart-success-modal';
import styles from './buy-box.module.scss';
import {
    CLPPurchaseOption,
    useClpPurchaseContext,
} from '../../context/clp-purchase-context.react-component';
import {CourseContentPlacement} from '@/features/course-landing-page/course-landing-page.react-component';
import {CourseIncludes} from '@/features/course-landing-page/components/course-includes/course-includes.react-component';
import {MarketplaceUfbNotice} from '@/features/course-landing-page/components/marketplace-purchase-content/marketplace-ufb-notice.react-component';
import {ClpSecondaryActions} from '@/features/course-landing-page/components/clp-secondary-actions';
import {RedeemCouponContainer} from '@/features/course-landing-page/components/redeem-coupon/redeem-coupon-container.react-component';
import {useFeatures} from '@udemy-v2/react-experimentation';

interface BuyBoxProps {
    placement: CourseContentPlacement;
}

/**
 * BuyBox component displays the purchase options for paid courses.
 * This component handles the rendering of the buy box in the sidebar and body content.
 *
 * Note: Wrapped with MobX observer to react to price store updates when
 * pricing data changes (e.g., after coupon application/removal).
 *
 * @param placement
 * @constructor
 */
export const BuyBox = observer(({placement}: BuyBoxProps) => {
    const {gettext} = useI18n();
    const course = useClpCourse();
    const coursePrice = useCoursePrice(Number(course.id));
    const {
        selectedPurchaseOption,
        isCourseInAvailablePlans,
        showLifetimeAccessInBuyBox,
        isReloadingPrice,
    } = useClpPurchaseContext();
    const isMobile = useMatchMedia('sm-max');
    const {featureVariantAssignments} = useFeatures(['cart_success_desktop']);
    const cartSuccessDesktopFeatureAssignment =
        featureVariantAssignments.get('cart_success_desktop')?.configuration;
    const isCartSuccessDesktopEnabled =
        cartSuccessDesktopFeatureAssignment?.skipAddToCartSuccessModal ?? false;
    // Mobile: always redirect to cart. Desktop: redirect only when cart_success_desktop feature is enabled.
    const forceRedirectToCart = !!isMobile || isCartSuccessDesktopEnabled;
    const isPaid = !course.isFree;

    // DON'T destructure coursePrice - access properties directly to maintain MobX reactivity
    // When reloading, don't consider the price as "loaded" to prevent showing stale data
    const isPriceLoaded = coursePrice?.status === 'loaded' && !isReloadingPrice;
    const isPersonalPlanPrimaryPurchaseOptions =
        selectedPurchaseOption === CLPPurchaseOption.SUBSCRIPTION;

    // Determine if there's a discount - matches the logic from the older implementation
    // Only show discount when price is fully loaded (not reloading)
    // Access properties directly from coursePrice to maintain MobX reactivity
    const showDiscountPrice =
        isPriceLoaded &&
        coursePrice?.discountPrice &&
        coursePrice?.listPrice &&
        coursePrice.discountPrice.amount !== coursePrice.listPrice.amount;

    const actionContainerClassName = classNames(styles['action-container'], {
        [styles['action-container-body']]: placement === CourseContentPlacement.BODY,
    });

    return (
        <div>
            <div className={styles.container} data-purpose="buy-box">
                <div className={styles['price-container']}>
                    {isReloadingPrice ? (
                        <TextSkeleton
                            lineCountPerParagraph={0}
                            className={styles['price-skeleton']}
                            withTitle={true}
                        />
                    ) : isPriceLoaded ? (
                        <PriceEventTrackingProvider
                            priceEventTrackingContext={{
                                priceType: PURCHASE_PRICE_TYPES.individual_buyable,
                                buyableType: PRICE_EVENTING_BUYABLE_TYPES.COURSE,
                                buyableId: Number(course.id),
                                buyableTrackingId: course.frontendTrackingId,
                            }}
                        >
                            <AsyncStaticPriceText
                                showDiscountDuration={true}
                                courseId={Number(course.id)}
                                listPriceString={coursePrice?.listPrice?.formattedPrice}
                                discountPriceString={coursePrice?.price?.formattedPrice}
                                discountPriceClassName={classNames(
                                    'ud-heading-xl',
                                    styles['discount-price'],
                                )}
                                percentDiscount={coursePrice?.discountPercentForDisplay}
                                percentDiscountClassName={classNames(
                                    'ud-text-md',
                                    styles['percent-discount'],
                                )}
                                listPriceClassName={classNames(
                                    showDiscountPrice ? 'ud-text-md' : 'ud-heading-xl',
                                    styles['list-price'],
                                )}
                                showPercentDiscount={showDiscountPrice}
                                className={classNames({
                                    [styles['price-text--with-discount']]: showDiscountPrice,
                                })}
                            />
                        </PriceEventTrackingProvider>
                    ) : null}
                </div>
                <div className={styles['buttons-container']}>
                    <div className={styles['horizontal-buttons']}>
                        <AddToCartButton
                            buyables={[
                                {
                                    id: Number(course.id),
                                    buyableObjectType: 'course',
                                    frontendTrackingId: course.frontendTrackingId,
                                },
                            ]}
                            buttonStyleProps={{
                                size: 'large',
                                udStyle: isPersonalPlanPrimaryPurchaseOptions
                                    ? 'secondary'
                                    : 'primary',
                            }}
                            addToCartSuccessModal={
                                !isMobile ? <ClpCartSuccessModal course={course} /> : undefined
                            }
                            forceRedirectToCart={!!forceRedirectToCart}
                        />
                        <WishlistButton courseId={Number(course.id)} />
                    </div>
                    <ClpPrimaryCtaButton udStyle="secondary" />
                </div>

                <div
                    className={classNames(styles['secondary-message'], 'ud-text-xs')}
                    data-purpose="money-back-guarantee"
                >
                    {gettext('30-Day Money-Back Guarantee')}
                </div>

                {showLifetimeAccessInBuyBox && (
                    <div
                        className={classNames(styles['secondary-message'], 'ud-text-xs')}
                        data-purpose="full-lifetime-access"
                    >
                        {gettext('Full Lifetime Access')}
                    </div>
                )}
                {CourseContentPlacement.SIDEBAR === placement && !isCourseInAvailablePlans ? (
                    <>
                        <CourseIncludes className={styles['course-includes']} />
                        <ClpSecondaryActions className={actionContainerClassName} />
                        <RedeemCouponContainer
                            className={actionContainerClassName}
                            is_paid={isPaid}
                            showCouponInputModeButton={false}
                        />
                        <MarketplaceUfbNotice />
                    </>
                ) : (
                    <>
                        <ClpSecondaryActions className={actionContainerClassName} />
                        <RedeemCouponContainer
                            className={actionContainerClassName}
                            is_paid={isPaid}
                            showCouponInputModeButton={false}
                        />
                    </>
                )}
            </div>
        </div>
    );
});
